/**
 * Shared types for Admin Support feature
 *
 * God-mode admin endpoints for company management, user onboarding,
 * bulk operations, and support tooling.
 */
import { z } from "zod";
// ===== COMPANY SEARCH =====
export const AdminSearchCompaniesInputSchema = z.object({
    search: z.string(),
    limit: z.coerce.number().int().positive().default(20),
});
export const AdminCompanyResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    employeeCount: z.number().int().nonnegative(),
});
export const AdminSearchCompaniesResponseSchema = z.object({
    data: z.array(AdminCompanyResponseSchema),
});
// ===== USER MANAGEMENT =====
export const AdminListCompanyUsersInputSchema = z.object({
    companyId: z.uuid(),
    search: z.string().optional(),
    isActive: z.coerce.boolean().optional(),
});
export const AdminCompanyUserResponseSchema = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().nullable(),
    isActive: z.boolean(),
    employmentStartDate: z.iso.datetime().nullable(),
    employmentEndDate: z.iso.datetime().nullable(),
    role: z.string(),
    locationNames: z.array(z.string()),
});
export const AdminListCompanyUsersResponseSchema = z.object({
    data: z.array(AdminCompanyUserResponseSchema),
});
// ===== INVITATION MANAGEMENT =====
export const AdminListCompanyInvitationsInputSchema = z.object({
    companyId: z.uuid(),
    search: z.string().optional(),
});
export const AdminInvitationResponseSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    status: z.enum(["PENDING", "EXPIRED", "USED"]),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime().nullable(),
    usedAt: z.iso.datetime().nullable(),
    activatedAt: z.iso.datetime().nullable(),
});
export const AdminListCompanyInvitationsResponseSchema = z.object({
    data: z.array(AdminInvitationResponseSchema),
});
// ===== ACTIONS =====
export const AdminSendWelcomeEmailInputSchema = z.object({
    companyId: z.uuid(),
    userId: z.uuid(),
});
export const AdminResetInvitationInputSchema = z.object({
    companyId: z.uuid(),
    invitationId: z.uuid(),
});
export const AdminTriggerPasswordResetInputSchema = z.object({
    companyId: z.uuid(),
    email: z.string(),
});
// ===== ONBOARD USER =====
export const AdminOnboardUserInputSchema = z.object({
    companyId: z.uuid(),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().optional(),
    locationId: z.uuid().optional(),
    jobRoleId: z.uuid().optional(),
    isDesktopUser: z.boolean().default(false),
});
export const AdminOnboardUserResponseSchema = z.object({
    userId: z.uuid(),
    employmentId: z.uuid(),
    email: z.string(),
});
// ===== DEACTIVATE / REACTIVATE =====
export const AdminDeactivateUserInputSchema = z.object({
    companyId: z.uuid(),
    employmentId: z.uuid(),
});
export const AdminReactivateUserInputSchema = z.object({
    companyId: z.uuid(),
    employmentId: z.uuid(),
});
// ===== COMPANY REFERENCE DATA =====
export const AdminGetCompanyLocationsInputSchema = z.object({
    companyId: z.uuid(),
});
export const AdminLocationResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
});
export const AdminGetCompanyLocationsResponseSchema = z.object({
    data: z.array(AdminLocationResponseSchema),
});
export const AdminGetCompanyJobRoleFamiliesInputSchema = z.object({
    companyId: z.uuid(),
});
export const AdminJobRoleFamilyResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
});
export const AdminGetCompanyJobRoleFamiliesResponseSchema = z.object({
    data: z.array(AdminJobRoleFamilyResponseSchema),
});
// ===== BULK SHIFTS =====
export const AdminBulkCreateShiftsInputSchema = z.object({
    companyId: z.uuid(),
    shifts: z.array(z.object({
        startTime: z.iso.datetime(),
        endTime: z.iso.datetime(),
        locationId: z.uuid(),
        jobRoleFamilyId: z.uuid(),
        employeeUserId: z.uuid().optional(),
        notes: z.string().optional(),
    })),
});
export const AdminBulkCreateShiftsResponseSchema = z.object({
    created: z.number().int().nonnegative(),
    errors: z.array(z.object({
        index: z.number().int().nonnegative(),
        message: z.string(),
    })),
});
// ===== BULK TIMECARDS =====
export const AdminBulkCreateTimecardsInputSchema = z.object({
    companyId: z.uuid(),
    timecards: z.array(z.object({
        shiftId: z.uuid(),
        employeeUserId: z.uuid(),
        locationId: z.uuid(),
        clockIn: z.iso.datetime(),
        clockOut: z.iso.datetime(),
        status: z.string().optional(),
        regularHours: z.number().optional(),
        overtimeHours: z.number().optional(),
    })),
});
export const AdminBulkCreateTimecardsResponseSchema = z.object({
    created: z.number().int().nonnegative(),
    errors: z.array(z.object({
        index: z.number().int().nonnegative(),
        message: z.string(),
    })),
});
// ===== SUCCESS RESPONSE =====
export const AdminSuccessResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
});
